import React, { useState, useRef, useEffect } from "react"
import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  CardBody,
  Button,
  Label,
  Input,
  FormFeedback,
  Form,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap"
import { useNavigate } from "react-router-dom"
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb"
import * as Yup from "yup"
import { useFormik } from "formik"
import { useSelector, useDispatch } from "react-redux"
import { createSelector } from "reselect"
import withRouter from "components/Common/withRouter"
import classnames from "classnames"
import AuthenticationApi from "Api/AuthenticationApi"
import { editProfile, resetProfileFlag } from "store/actions"
import { Toast } from "primereact/toast"
import "./SiteSettings.scss"
import { Dropdown } from "primereact/dropdown"
import MediaConfigurationApi from "Api/MediaConfigurationApi"
import SiteSettingsApi from "Api/SiteSettings"
import { Editor } from "@tinymce/tinymce-react"

const imageTypeOptions = [
  { label: "JPG", value: "JPG" },
  { label: "PNG", value: "PNG" },
  { label: "GIF", value: "GIF" },
]

const videoTypeOptions = [
  { label: "MP4", value: "MP4" },
  { label: "WebM", value: "WebM" },
  { label: "OGG", value: "OGG" },
]

const documentTypeOptions = [
  { label: "PDF", value: "PDF" },
  { label: "DOC", value: "DOC" },
  { label: "DOCX", value: "DOCX" },
]

const SiteSettings = () => {
  document.title = "Site Settings"
  const accessToken = localStorage.getItem("accessToken")
  
  const toast = useRef(null)
  const [activeTab, setActiveTab] = useState("1")
  const [loading, setLoading] = useState(false)
  const [maintenanceMode, setMaintenanceMode] = useState(false)
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false)
  const [privacyContent, setPrivacyContent] = useState("")
  const userData = JSON.parse(localStorage.getItem("userData"))
  const permissions = userData?.permission || []
  const [privacyPolicyData, setPrivacyPolicyData] = useState({
    content: "",
    createdBy: null,
    createdAt: null,
    updatedBy: null,
    updatedAt: null,
  })

  const [showTermsOfService, setShowTermsOfService] = useState(false)
  const [termsOfServiceData, setTermsOfServiceData] = useState({
    content: "",
    createdBy: null,
    createdAt: null,
    updatedBy: null,
    updatedAt: null,
  })
  const [termsContent, setTermsContent] = useState("")

  const [mediaConfig, setMediaConfig] = useState({
    image: {
      size: "",
      allowedTypes: "",
    },
    video: {
      size: "",
      allowedTypes: "",
    },
    document: {
      size: "",
      allowedTypes: "",
    },
  })
  const [otpConfig, setOtpConfig] = useState({
    phoneOtpTime: "",
    emailOtpTime: "",
    loginOtpTime: "",
  })

  const [maintenanceSettings, setMaintenanceSettings] = useState({
    isEnabled: false,
    message: "Site is under maintenance. Please try again later.",
  })
  const handleMaintenanceChange = (field, value) => {
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }

    SiteSettingsApi.serviceMaintenance({ siteMaintenance: value }, config)
      .then(response => {
        setMaintenanceSettings(prev => ({
          ...prev,
          [field]: value,
        }))

        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Maintenance mode updated successfully",
          life: 3000,
        })
      })
      .catch(error => {
        console.error("Error updating maintenance mode:", error)

        toast.current.show({
          severity: "error",
          summary: "Error",
          detail:
            error.response?.data?.message ||
            "Failed to update maintenance mode",
          life: 3000,
        })

        setMaintenanceSettings(prev => ({
          ...prev,
          [field]: !value,
        }))
      })
  }

  const handleUpdateOtpConfig = async () => {
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }

      const response = await SiteSettingsApi.updateOtpExpiry(
        {
          loginOtpExpiry: otpConfig.loginOtpTime,
          emailOtpExpiry: otpConfig.emailOtpTime,
          phoneOtpExpiry: otpConfig.phoneOtpTime,
        },
        config,
      )

      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "OTP configuration updated successfully",
        life: 3000,
      })
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail:
          error.response?.data?.message || "Failed to update OTP configuration",
        life: 3000,
      })
    }
  }

  useEffect(() => {
    const headers = {
      Authorization: `Bearer ${accessToken}`,
    }
    MediaConfigurationApi.getMediaConfiguration(headers)
      .then(res => {
        setMediaConfig(res.data.data)
      })
      .catch(err => {
      })
  }, [accessToken])
  useEffect(() => {
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }

    SiteSettingsApi.getServiceMaintenance(config)
      .then(response => {
        const currentStatus = !!response.data?.siteMaintenance
        setMaintenanceSettings(prev => ({
          ...prev,
          isEnabled: currentStatus,
        }))
      })
      .catch(error => {
        console.error("Error fetching service maintenance:", error)
      })
  }, [accessToken])
  useEffect(() => {
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }

    SiteSettingsApi.getOtpDetails(config)
      .then(response => {
        const otpDetails = response.data.data
        setOtpConfig({
          phoneOtpTime: otpDetails.phoneOtpExpiry,
          emailOtpTime: otpDetails.emailOtpExpiry,
          loginOtpTime: otpDetails.loginOtpExpiry,
        })
      })
      .catch(error => {
        console.error("Error fetching OTP details:", error)
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to fetch OTP configuration",
          life: 3000,
        })
      })
  }, [accessToken])

  useEffect(() => {
    const fetchPrivacyPolicy = async () => {
      try {
        const config = {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
        const response = await SiteSettingsApi.getPrivacyPolicy(config)
        setPrivacyPolicyData(response.data.data)
        setPrivacyContent(response.data.data.content)
      } catch (error) {
        console.error("Error fetching privacy policy:", error)
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to fetch privacy policy",
          life: 3000,
        })
      }
    }
    fetchPrivacyPolicy()
  }, [accessToken])

  useEffect(() => {
    const fetchTermsOfService = async () => {
      try {
        const config = {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
        const response = await SiteSettingsApi.getTermsOfService(config)
        setTermsOfServiceData(response.data.data)
        setTermsContent(response.data.data.content)
      } catch (error) {
        console.error("Error fetching terms of service:", error)
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to fetch terms of service",
          life: 3000,
        })
      }
    }
    fetchTermsOfService()
  }, [accessToken])

  const handleConfigChange = (configType, field, value) => {
    setMediaConfig(prev => ({
      ...prev,
      [configType]: {
        ...prev[configType],
        [field]: value,
      },
    }))
  }

  const toggleTab = tab => {
    if (activeTab !== tab) setActiveTab(tab)
  }

  const handleUpdateConfiguration = () => {
    const validateConfig = config => {
      return config.size && config.allowedTypes
    }

    if (
      !validateConfig(mediaConfig.image) ||
      !validateConfig(mediaConfig.video) ||
      !validateConfig(mediaConfig.document)
    ) {
      toast.current.show({
        severity: "error",
        summary: "Validation Error",
        detail: "All configuration fields are required",
        life: 3000,
      })
      return
    }
    const headers = {
      Authorization: `Bearer ${accessToken}`,
    }
    MediaConfigurationApi.createMediaConfiguration(mediaConfig, headers)
      .then(res => {
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Configuration updated successfully",
          life: 3000,
        })
      })
      .catch(err => {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to update configuration",
          life: 3000,
        })
      })
  }
  const handleReloadUsers = async () => {
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }

      const response = await SiteSettingsApi.updateAllStudentsIsAgeTeen(config)

      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: `Successfully updated ${response.data.data.updatedStudents} out of ${response.data.data.totalStudents} students`,
        life: 3000,
      })
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to reload users",
        life: 3000,
      })
    }
  }

  const handleUpdatePrivacyPolicy = async () => {
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }

      const response = await SiteSettingsApi.updatePrivacyPolicy(
        { privacyPolicy: privacyContent },
        config,
      )

      if (response.data.success) {
        setPrivacyPolicyData(response.data.data)
        setShowPrivacyPolicy(false)
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Privacy policy updated successfully",
          life: 3000,
        })
      }
    } catch (error) {
      console.error("Error updating privacy policy:", error)
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail:
          error.response?.data?.message || "Failed to update privacy policy",
        life: 3000,
      })
    }
  }

  const handleUpdateTermsOfService = async () => {
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
      const response = await SiteSettingsApi.updateTermsOfService(
        { termsOfService: termsContent },
        config,
      )

      if (response.data.success) {
        setTermsOfServiceData(response.data.data)
        setShowTermsOfService(false)
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Terms of service updated successfully",
          life: 3000,
        })
      }
    } catch (error) {
      console.error("Error updating terms of service:", error)
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail:
          error.response?.data?.message || "Failed to update terms of service",
        life: 3000,
      })
    }
  }
  const hasPermission = (permissions, resource, method) => {
    if (!Array.isArray(permissions.permission)) {
      console.error("Permissions is not an array:", permissions)
      return false
    }

    return permissions.permission.some(
      perm =>
        perm.permissionName === resource &&
        perm.permissionType.includes(method),
    )
  }

  const navigate = useNavigate()
  
  const breadcrumbItems = [
    { label: "Pixwik", link: "/" },
    { label: "Site Settings", link: "#" },
  ]

  const handleBreadcrumbClick = link => {
    if (link !== "#") {
      navigate(link)
    }
  }

  return (
    <>
      <Toast ref={toast} />
      <Row>
        <Col xs="12">
          <div className="site-settings-header d-flex align-items-center justify-content-between">
            <CustomBreadCrumb
              data={breadcrumbItems}
              pageTitle="Site Settings"
              handleClick={handleBreadcrumbClick}
            />
          </div>

          <Nav tabs className="nav-tabs-custom mt-4 mb-3">
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === "1" })}
                    onClick={() => toggleTab("1")}
                  >
                    Site Configuration
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === "2" })}
                    onClick={() => toggleTab("2")}
                  >
                    Media Configuration
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === "3" })}
                    onClick={() => toggleTab("3")}
                  >
                    Attention
                  </NavLink>
                </NavItem>
              </Nav>

              <TabContent activeTab={activeTab}>
                <TabPane tabId="2">
                  <Row>
                    <Col lg="12">
                      {(userData.role === "admin" || hasPermission(permissions, "mediaConfiguration", "GET")) && (
                        <Card>
                          <CardBody>
                            <div className="d-flex flex-column gap-2">
                              <div className="configuration-container d-flex flex-column gap-1">
                                <h5 className="m-0">Image Configuration</h5>
                                <div className="d-flex align-items-center gap-3">
                                  <div className="image-size flex-1">
                                    <label>Image Size</label>
                                    <Input
                                      type="number"
                                      value={mediaConfig.image.size}
                                      onChange={e =>
                                        handleConfigChange(
                                          "image",
                                          "size",
                                          e.target.value,
                                        )
                                      }
                                    />
                                  </div>
                                  <div className="image-type flex-1">
                                    <label>Image Type</label>
                                    <Dropdown
                                      value={mediaConfig.image.allowedTypes}
                                      options={imageTypeOptions}
                                      onChange={e =>
                                        handleConfigChange(
                                          "image",
                                          "allowedTypes",
                                          e.value,
                                        )
                                      }
                                      placeholder="Select Image Type"
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="configuration-container d-flex flex-column gap-1">
                                <h5 className="m-0">Video Configuration</h5>
                                <div className="d-flex align-items-center gap-3">
                                  <div className="image-size flex-1">
                                    <label>Video Size</label>
                                    <Input
                                      type="number"
                                      value={mediaConfig.video.size}
                                      onChange={e =>
                                        handleConfigChange(
                                          "video",
                                          "size",
                                          e.target.value,
                                        )
                                      }
                                    />
                                  </div>
                                  <div className="image-type flex-1">
                                    <label>Video Type</label>
                                    <Dropdown
                                      value={mediaConfig.video.allowedTypes}
                                      options={videoTypeOptions}
                                      onChange={e =>
                                        handleConfigChange(
                                          "video",
                                          "allowedTypes",
                                          e.value,
                                        )
                                      }
                                      placeholder="Select Video Type"
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="configuration-container d-flex flex-column gap-1">
                                <h5 className="m-0">Document Configuration</h5>
                                <div className="d-flex align-items-center gap-3">
                                  <div className="image-size flex-1">
                                    <label>Document Size</label>
                                    <Input
                                      type="number"
                                      value={mediaConfig.document.size}
                                      onChange={e =>
                                        handleConfigChange(
                                          "document",
                                          "size",
                                          e.target.value,
                                        )
                                      }
                                    />
                                  </div>
                                  <div className="image-type flex-1">
                                    <label>Document Type</label>
                                    <Dropdown
                                      value={mediaConfig.document.allowedTypes}
                                      options={documentTypeOptions}
                                      onChange={e =>
                                        handleConfigChange(
                                          "document",
                                          "allowedTypes",
                                          e.value,
                                        )
                                      }
                                      placeholder="Select Document Type"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="d-flex justify-content-end">
                              {(userData.role === "admin" || hasPermission(permissions, "mediaConfiguration", "PUT")) && (
                                <Button
                                  color="primary"
                                  className="mt-3"
                                  onClick={handleUpdateConfiguration}
                                >
                                  Update Configuration
                                </Button>
                              )}
                            </div>
                          </CardBody>
                        </Card>
                      )}
                    </Col>
                  </Row>
                </TabPane>
                <TabPane tabId="1">
                  <Row>
                    <Col lg="12">
                      <Card>
                        <CardBody>
                          <div className="d-flex flex-column gap-4">
                            {(userData.role === "admin" || hasPermission(permissions, "siteMaintenance", "GET")) && (
                              <div>
                                <div className="d-flex align-items-center gap-2 mb-0">
                                  <h5 className="mb-0">Site Maintenance</h5>
                                </div>
                                <div className="form-control mt-4">
                                  <Row>
                                    <Col md={12}>
                                      <div className="d-flex align-items-center mb-2 gap-3">
                                        <Label className="mb-0">
                                          Enable Maintenance Mode
                                        </Label>
                                        <div className="form-check form-switch">
                                          {(userData.role === "admin" || hasPermission(permissions, "siteMaintenance", "PUT")) && (
                                            <Input
                                              type="switch"
                                              role="switch"
                                              checked={maintenanceSettings.isEnabled}
                                              onChange={() => handleMaintenanceChange("isEnabled", !maintenanceSettings.isEnabled)}
                                            />
                                          )}
                                        </div>
                                      </div>
                                      {maintenanceSettings.isEnabled && (
                                        <span className="text-danger mb-0">
                                          *Site is under maintenance.
                                        </span>
                                      )}
                                    </Col>
                                  </Row>
                                </div>
                              </div>
                            )}
                            <hr className="my-2" />
                            {(userData.role === "admin" || hasPermission(permissions, "otpConfiguration", "GET")) && (
                              <div>
                                <div className="d-flex flex-column gap-4">
                                  <div className="d-flex align-items-center gap-2">
                                    <h5 className="mb-0">OTP Configuration</h5>
                                  </div>
                                  <div className="form-control mt-2">
                                    <Row className="otp-config-container">
                                      <Col md={4}>
                                        <div className="form-group">
                                          <Label>Phone OTP Expiry Time (seconds)</Label>
                                          <Input
                                            type="number"
                                            value={otpConfig.phoneOtpTime}
                                            onChange={e => setOtpConfig(prev => ({
                                              ...prev,
                                              phoneOtpTime: e.target.value,
                                            }))}
                                            placeholder="Enter time in seconds"
                                            min="0"
                                            disabled={!(userData.role === "admin" || hasPermission(permissions, "otpConfiguration", "PUT"))}
                                          />
                                          <small className="text-muted">
                                            Current Time: {Math.floor(otpConfig.phoneOtpTime / 60)} minutes
                                          </small>
                                        </div>
                                      </Col>
                                      <Col md={4}>
                                        <div className="form-group">
                                          <Label>Email OTP Expiry Time (seconds)</Label>
                                          <Input
                                            type="number"
                                            value={otpConfig.emailOtpTime}
                                            onChange={e => setOtpConfig(prev => ({
                                              ...prev,
                                              emailOtpTime: e.target.value,
                                            }))}
                                            placeholder="Enter time in seconds"
                                            min="0"
                                            disabled={!(userData.role === "admin" || hasPermission(permissions, "otpConfiguration", "PUT"))}
                                          />
                                          <small className="text-muted">
                                            Current Time: {Math.floor(otpConfig.emailOtpTime / 60)} minutes
                                          </small>
                                        </div>
                                      </Col>
                                    </Row>
                                    <div className="d-flex justify-content-end">
                                      {(userData.role === "admin" || hasPermission(permissions, "otpConfiguration", "PUT")) && (
                                        <Button
                                          color="primary"
                                          onClick={handleUpdateOtpConfig}
                                        >
                                          Update OTP Configuration
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                </TabPane>
                <TabPane tabId="3">
                  <Row>
                    <Col lg="12">
                      <Card>
                        {(userData.role === "admin" || hasPermission(permissions, "users", "PUT")) && (
                          <CardBody>
                            <h5 className="mb-0">Users</h5>
                            <div className="form-control mt-4 d-flex justify-content-between align-items-center">
                              <span className="m-2">
                                update age status for all students
                              </span>
                              <button
                                className="btn btn-primary justify-content-end"
                                onClick={handleReloadUsers}
                              >
                                Reload
                              </button>
                            </div>
                          </CardBody>
                        )}
                        {(userData.role === "admin" || hasPermission(permissions, "privacyPolicy", "GET")) && (
                          <CardBody>
                            <h5 className="mb-0">Privacy Policy</h5>
                            <div className="form-control mt-4">
                              <div className="d-flex justify-content-between align-items-center">
                                <h6 className="mb-0">
                                  Manage privacy policy content
                                </h6>
                                {(userData.role === "admin" || hasPermission(permissions, "privacyPolicy", "PUT")) && (
                                  <Button
                                    color="primary"
                                    onClick={() => setShowPrivacyPolicy(!showPrivacyPolicy)}
                                  >
                                    {showPrivacyPolicy ? "Close Editor" : "Edit"}
                                  </Button>
                                )}
                              </div>
                              {showPrivacyPolicy && (
                                <div className="mt-4">
                                  <Editor
                                    apiKey="t7g4d6ut5ygdvwif7tp5bwyysui56413aomjuh8kusvw5qgv"
                                    value={privacyContent}
                                    onEditorChange={content =>
                                      setPrivacyContent(content)
                                    }
                                    init={{
                                      height: 500,
                                      menubar: true,
                                      plugins: [
                                        "advlist",
                                        "autolink",
                                        "lists",
                                        "link",
                                        "image",
                                        "charmap",
                                        "preview",
                                        "anchor",
                                        "code",
                                        "fullscreen",
                                        "table",
                                        "help",
                                        "wordcount",
                                      ],
                                      toolbar:
                                        "undo redo | formatselect | bold italic backcolor | " +
                                        "alignleft aligncenter alignright alignjustify | " +
                                        "bullist numlist outdent indent | removeformat | help",
                                      content_css: false,
                                      content_style: "",
                                      valid_elements: "*[*]",
                                    }}
                                  />
                                  <div className="d-flex justify-content-end mt-3">
                                    <Button
                                      color="secondary"
                                      className="me-2"
                                      onClick={() => {
                                        setShowPrivacyPolicy(false)
                                        setPrivacyContent(
                                          privacyPolicyData.content,
                                        )
                                      }}
                                    >
                                      Cancel
                                    </Button>
                                    <Button
                                      color="primary"
                                      onClick={handleUpdatePrivacyPolicy}
                                    >
                                      Save Changes
                                    </Button>
                                  </div>
                                </div>
                              )}
                              {!showPrivacyPolicy &&
                                privacyPolicyData.content && (
                                  <div className="mt-4">
                                    <div
                                      dangerouslySetInnerHTML={{
                                        __html: privacyPolicyData.content,
                                      }}
                                    />
                                    <div className="mt-3 text-muted">
                                      <small>
                                        Last updated by:{" "}
                                        {privacyPolicyData.updatedBy?.userName ||
                                          "N/A"}
                                        {privacyPolicyData.updatedAt &&
                                          ` on ${new Date(privacyPolicyData.updatedAt).toLocaleString()}`}
                                      </small>
                                    </div>
                                  </div>
                                )}
                            </div>
                          </CardBody>
                        )}
                        {(userData.role === "admin" || hasPermission(permissions, "termsOfService", "GET")) && (
                          <CardBody>
                            <h5 className="mb-0">Terms of Service</h5>
                            <div className="form-control mt-4">
                              <div className="d-flex justify-content-between align-items-center">
                                <h6 className="mb-0">
                                  Manage terms of service content
                                </h6>
                                {(userData.role === "admin" || hasPermission(permissions, "termsOfService", "PUT")) && (
                                  <Button
                                    color="primary"
                                    onClick={() => setShowTermsOfService(!showTermsOfService)}
                                  >
                                    {showTermsOfService ? "Close Editor" : "Edit"}
                                  </Button>
                                )}
                              </div>
                              {showTermsOfService && (
                                <div className="mt-4">
                                  <Editor
                                    apiKey="t7g4d6ut5ygdvwif7tp5bwyysui56413aomjuh8kusvw5qgv"
                                    value={termsContent}
                                    onEditorChange={content =>
                                      setTermsContent(content)
                                    }
                                    init={{
                                      height: 500,
                                      menubar: true,
                                      plugins: [
                                        "advlist",
                                        "autolink",
                                        "lists",
                                        "link",
                                        "image",
                                        "charmap",
                                        "preview",
                                        "anchor",
                                        "code",
                                        "fullscreen",
                                        "table",
                                        "help",
                                        "wordcount",
                                      ],
                                      toolbar:
                                        "undo redo | formatselect | bold italic backcolor | " +
                                        "alignleft aligncenter alignright alignjustify | " +
                                        "bullist numlist outdent indent | removeformat | help",
                                      content_css: false,
                                      content_style: "",
                                      valid_elements: "*[*]",
                                    }}
                                  />
                                  <div className="d-flex justify-content-end mt-3">
                                    <Button
                                      color="secondary"
                                      className="me-2"
                                      onClick={() => {
                                        setShowTermsOfService(false)
                                        setTermsContent(
                                          termsOfServiceData.content,
                                        )
                                      }}
                                    >
                                      Cancel
                                    </Button>
                                    <Button
                                      color="primary"
                                      onClick={handleUpdateTermsOfService}
                                    >
                                      Save Changes
                                    </Button>
                                  </div>
                                </div>
                              )}
                              {!showTermsOfService &&
                                termsOfServiceData.content && (
                                  <div className="mt-4">
                                    <div
                                      dangerouslySetInnerHTML={{
                                        __html: termsOfServiceData.content,
                                      }}
                                    />
                                    <div className="mt-3 text-muted">
                                      <small>
                                        Last updated by:{" "}
                                        {termsOfServiceData.updatedBy?.userName ||
                                          "N/A"}
                                        {termsOfServiceData.updatedAt &&
                                          ` on ${new Date(termsOfServiceData.updatedAt).toLocaleString()}`}
                                      </small>
                                    </div>
                                  </div>
                                )}
                            </div>
                          </CardBody>
                        )}
                      </Card>
                    </Col>
                  </Row>
                </TabPane>
              </TabContent>
        </Col>
      </Row>
    </>
  )
}

export default SiteSettings
